import { AnnotationShape } from '@/web-components/import-wizard/typings';
import { SmallDetailsPayload } from '@/typings';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useShapeStore } from '@/stores/shape';
import imageProcessingAPI from '@/services/FileService';
import { base64StringToBlob } from 'blob-util';
import {
  CheckImageSmallDetailsCommand,
  TextAlignment,
} from '@/typings/SmallDetails';
import { px2mm } from '@/utils';
import { DefaultSmallDetailsResult } from '@/typings/SmallDetails';
import { SmallDetailsStatus } from '@midocean/webapps-shared-domain';
//If limits are updated - do not forget to update backend
export function getSmallDetailsStatus(
  positiveLoss: number,
  negativeLoss: number,
  calculatedQuality?: number
): SmallDetailsStatus {
  if (positiveLoss + negativeLoss > 10) {
    return 'Bad';
  } else if (positiveLoss + negativeLoss > 0.3) {
    return 'Average';
  } else if (
    calculatedQuality === 0 &&
    positiveLoss === 0 &&
    negativeLoss === 0
  ) {
    return 'NoCheck';
  } else {
    return 'Good';
  }
}

export function getSmallDetailsValidatorColor(
  status: SmallDetailsStatus
): 'Red' | 'Green' | 'Orange' {
  switch (status) {
    case 'Good':
      return 'Green';
    case 'Average':
      return 'Orange';
    case 'Bad':
      return 'Red';
    default:
      return 'Green';
  }
}

export function getShapesForRevalidation(): AnnotationShape[] {
  const shapeStore = useShapeStore();
  return shapeStore.shapes.filter(
    (x) => x.smallDetailsResult && x.smallDetailsResult.isCheckValid === false
  );
}

export async function revalidateSmallDetails(): Promise<void> {
  const shapeStore = useShapeStore();
  const productStore = useProductStore();

  const shapesToRevalidate = getShapesForRevalidation();

  if (shapesToRevalidate.length > 0) {
    const results = await Promise.all(
      shapesToRevalidate.map(async (shape) => {
        if (!shape.smallDetailsResult?.isCheckValid) {
          const positionId = shapeStore.positionShapeMap.find(
            (x) => x.logoId === shape.id
          )?.id;
          if (positionId) {
            const printPosition = productStore.data.productPrintPositions.find(
              (x) => x.id === positionId
            );
            if (printPosition) {
              const widthInMM = px2mm(
                shape.width as number,
                printPosition.originalImageWebDpi
              );
              const heightInMM = px2mm(
                shape.height as number,
                printPosition.originalImageWebDpi
              );
              const techniqueId = productStore.unavailablePrintPositions.find(
                (x) => x.positionId === positionId
              )?.technique.id;
              const limitation = printPosition.printTechniques.find(
                (x) => x.id === techniqueId
              )?.printTechnique.limitation;
              if (limitation) {
                let command: CheckImageSmallDetailsCommand;
                let blob: Blob;
                // Handle logo case
                if (shape.kind === 'Logo') {
                  command = {
                    id: shape.id,
                    logoWidthInMm: widthInMM,
                    positiveLimitInMm: limitation.positiveLimitMM,
                    negativeLimitInMm: limitation.negativeLimitMM,
                    ignoreColors:
                      limitation.ignoreColors ||
                      Array.from(
                        new Set(shape.pmsColorsInPngFile.map((obj) => obj.name))
                      ).length === 1,
                  };
                  blob = shape.pngFileWithoutConverters as Blob;
                } else {
                  command = {
                    id: shape.id,
                    logoWidthInMm: shape.originalWidthMM,
                    positiveLimitInMm: limitation.positiveLimitMM,
                    negativeLimitInMm: limitation.negativeLimitMM,
                    ignoreColors: limitation.ignoreColors,
                  };
                  blob = shape.pngFileWithoutConverters as Blob;
                }

                const { data } =
                  await imageProcessingAPI.checkImageSmallDetails(
                    command,
                    blob
                  );

                const smallDetails = {
                  ...data,
                  pngFile: data.pngFile !== null ? data.pngFile : '',
                  pngFileBlob:
                    data.pngFile !== null
                      ? base64StringToBlob(data.pngFile, 'image/png')
                      : new Blob(),
                  isCheckValid: true,
                  approvedAt: undefined,
                  id: data.id,
                  status: getSmallDetailsStatus(
                    data.positiveDetailsLoss,
                    data.negativeDetailsLoss,
                    data.calculatedQuality
                  ),
                } as SmallDetailsPayload;

                return { id: shape.id, smallDetails };
              }
            }
          }
        }
      })
    );
    shapeStore.$patch((state) => {
      results.forEach((x) => {
        if (x) {
          const shapeIdx = state.shapes.findIndex((p) => p.id === x.id);
          state.shapes.splice(shapeIdx, 1, {
            ...state.shapes[shapeIdx],
            smallDetailsResult: {
              positiveDetailsLoss: x.smallDetails.positiveDetailsLoss,
              negativeDetailsLoss: x.smallDetails.negativeDetailsLoss,
              calculatedQuality: x.smallDetails.calculatedQuality,
              approvedAt: x.smallDetails.approvedAt,
              status: x.smallDetails.status,
              pngFile: x.smallDetails.pngFile,
              pngFileBlob: x.smallDetails.pngFileBlob,
              isCheckValid: true,
              id: x.smallDetails.id,
            },
          });
        }
      });
    });
  }
}

export function mapSmallDetailsPayload(
  shape: AnnotationShape,
  isSmallDetailsTech: boolean
): SmallDetailsPayload | undefined {
  if (isSmallDetailsTech) {
    if (shape.smallDetailsResult) {
      return { ...shape.smallDetailsResult, isCheckValid: false };
    } else {
      return { ...DefaultSmallDetailsResult, id: shape.id };
    }
  } else {
    return undefined;
  }
}
